import { useState } from 'react'
import { Outlet, Link, NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SignedIn, SignedOut, SignInButton, UserButton } from '@clerk/clerk-react'
import { Menu, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { Logo } from '@/components/Logo'
import { WorkspaceSwitcher } from '@/components/WorkspaceSwitcher'
import { cn } from '@/lib/utils'

export default function AppLayout() {
  const { t } = useTranslation()
  const [mobileOpen, setMobileOpen] = useState(false)

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <nav className="sticky top-0 z-50 border-b bg-background/90 backdrop-blur-sm px-4 sm:px-6 py-3">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1.5 group">
            <Logo variant="icon" className="h-7 text-primary transition-opacity group-hover:opacity-80" />
            <span className="font-semibold text-base tracking-tight">Clausule</span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-0.5">
            <SignedIn>
              {(['dashboard', 'upload', 'settings'] as const).map((route) => (
                <NavLink
                  key={route}
                  to={`/${route}`}
                  className={({ isActive }) =>
                    cn(
                      'text-sm font-medium px-3 py-1.5 rounded-md transition-colors',
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                    )
                  }
                >
                  {t(`nav.${route}`)}
                </NavLink>
              ))}
              <div className="ml-1">
                <WorkspaceSwitcher />
              </div>
            </SignedIn>
            <div className="ml-1">
              <LanguageSwitcher />
            </div>
            <SignedIn>
              <div className="ml-1">
                <UserButton afterSignOutUrl="/" />
              </div>
            </SignedIn>
            <SignedOut>
              <SignInButton mode="modal">
                <Button variant="outline" size="sm" className="ml-1">
                  {t('nav.signIn')}
                </Button>
              </SignInButton>
            </SignedOut>
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            data-testid="mobile-menu-button"
            className="md:hidden p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMobileOpen((o) => !o)}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Mobile panel */}
        {mobileOpen && (
          <div className="md:hidden border-t mt-3 pt-3 pb-2 space-y-1 max-w-6xl mx-auto">
            <SignedIn>
              {(['dashboard', 'upload', 'settings'] as const).map((route) => (
                <NavLink
                  key={route}
                  to={`/${route}`}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'block text-sm font-medium px-3 py-2 rounded-md transition-colors',
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                    )
                  }
                >
                  {t(`nav.${route}`)}
                </NavLink>
              ))}
              <div className="px-1 pt-1">
                <WorkspaceSwitcher />
              </div>
            </SignedIn>
            <div className="flex items-center gap-2 px-1 pt-1">
              <LanguageSwitcher />
              <SignedIn>
                <UserButton afterSignOutUrl="/" />
              </SignedIn>
              <SignedOut>
                <SignInButton mode="modal">
                  <Button variant="outline" size="sm">
                    {t('nav.signIn')}
                  </Button>
                </SignInButton>
              </SignedOut>
            </div>
          </div>
        )}
      </nav>
      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  )
}
